import { yupResolver } from '@hookform/resolvers/yup'
import { useDisclosure } from '@nextui-org/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import * as yup from 'yup'
import { deleteFetcher, postWithFormDataFetcher, putWithFormDataFetcher } from '../services/api'
import { dirtyInput, isResponseErrorObject } from '../services/helpers'
import useAuth from './useAuth'

export default function useGallery({ mutate }) {
    const { user: userData } = useAuth()

    const getVideoEmbedUrl = (url) => {
        const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?youtu(?:\.be\/|be\.com\/(?:watch\?v=|embed\/|v\/|.+\?v=))([^&\n?#]+)/;
        const vimeoRegex = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/;
        const videoFileRegex = /\.(mp4|webm|ogg)(\?.*)?$/;
    
        if (youtubeRegex.test(url)) {
            const videoId = url.match(youtubeRegex)[1];
            return { type: 'youtube', url: `https://www.youtube.com/embed/${videoId}` };
        } else if (vimeoRegex.test(url)) {
            const videoId = url.match(vimeoRegex)[1];
            return { type: 'vimeo', url: `https://player.vimeo.com/video/${videoId}` };
        } else if (videoFileRegex.test(url)) {
            return { type: 'file', url };
        } else {
            return { type: 'invalid', url: '' };
        }
    };

    const formSchema = yup
        .object()
        .shape({
            title: yup.string().required('Harus diisi'),
            categories: yup.string().required('Harus diisi'),
            attachments: yup.mixed().required('Harus diisi'),
            videoLink: yup.string().nullable(),
            author: yup.string().required('Harus diisi'),
        })
        .required()

    const {
        isOpen: isOpenModalForm,
        onOpen: onOpenModalForm,
        onOpenChange: onOpenChangeModalForm,
        onClose: onCloseModalForm,
    } = useDisclosure()
    const { isOpen: isOpenModalAlert, onOpenChange: onOpenChangeModalAlert } = useDisclosure()
    const [editId, setEditId] = useState(null)
    const [isEdit, setIsEdit] = useState(false)
    const {
        register,
        handleSubmit,
        reset,
        control,
        setValue,
        getValues,
        watch,
        formState: { errors, isSubmitting, dirtyFields },
    } = useForm({ resolver: yupResolver(formSchema) })

    function onClickEdit(item) {
        setEditId(item.id)
        setIsEdit(true)
        reset({
            title: item.title,
            categories: item.categories[0]?.id,
            attachments: item.photoUrls,
            author: item.author,
            videoLink: item.videoLink,
        })
        onOpenChangeModalForm()
    }

    function onCloseForm() {
        setEditId(null)
        setIsEdit(false)
        reset({
            title: '',
            categories: '',
            videoLink: '',
            attachments: [],
            author: '',
        })
        onCloseModalForm()
    }

    function onClickDelete(id) {
        setEditId(id)
        onOpenChangeModalAlert()
    }

    async function onSubmitDelete() {
        try {
            await deleteFetcher('/api/content/gallery', editId)
            mutate()
            toast.success('Galeri berhasil dihapus!')
        } catch (error) {
            toast.error('Gagal hapus Galeri!')
        }
    }

    async function onSubmitForm(data) {
        try {
            if (isEdit) {
                const filteredData = dirtyInput(dirtyFields, data)

                const formData = new FormData()

                // Loop over filteredData to append fields to formData
                Object.keys(filteredData).forEach((key) => {
                    const value = filteredData[key]

                    // Special handling for 'attachments' field (which is an array of files)
                    if (key === 'attachments' && Array.isArray(value)) {
                        if (value.length > 0) {
                            value.forEach((file, index) => {
                                console.log(`Appending file ${index + 1}:`, file)
                                if (file instanceof File) {
                                    formData.append('attachments', file)
                                } else {
                                    console.error('Invalid file object:', file)
                                }
                            })
                        } else {
                            console.warn('No attachments found.')
                        }
                    } 
                    else if(key === 'videoLink' && value !== null) {
                        formData.append('videoLink', getVideoEmbedUrl(value)?.url)
                    } 
                    else if (value !== undefined && value !== null) {
                        formData.append(key, value)
                    }
                })

                formData.append('createdById', userData.id)
                formData.append('status', 'PUBLISHED')

                await putWithFormDataFetcher('/api/content/gallery', editId, formData)
                mutate()
                toast.success('Galeri berhasil diubah!')
            } else {
                const formData = new FormData()

                console.log(data.attachments)

                for (let i = 0; i < data.attachments.length; i++) {
                    formData.append('attachments', data.attachments[i])
                }
                
                formData.append('title', data.title)
                formData.append('author', data.author)
                formData.append('createdById', userData.id)
                formData.append('categories', data.categories)
                formData.append('status', 'PUBLISHED')
                if (data?.videoLink) {
                    formData.append('videoLink',getVideoEmbedUrl( data.videoLink)?.url)
                }

                await postWithFormDataFetcher('/api/content/gallery', formData)
                mutate()
                toast.success('Galeri berhasil ditambah!')
            }
            onCloseForm()
        } catch (error) {
            isResponseErrorObject(error.response.data.message)
                ? Object.entries(error.response.data.message).forEach(([key, value]) => {
                      toast.error(value)
                  })
                : toast.error(error.response.data.message)
        }
    }

    return {
        modalForm: {
            isOpenModalForm,
            onOpenModalForm,
            onOpenChangeModalForm,
        },
        modalAlert: {
            isOpenModalAlert,
            onOpenChangeModalAlert,
        },
        hookForm: {
            register,
            handleSubmit,
            reset,
            control,
            getValues,
            setValue,
            watch,
            formState: { errors, isSubmitting, dirtyFields },
        },
        isEdit,
        onClickEdit,
        onCloseForm,
        onClickDelete,
        onSubmitDelete,
        onSubmitForm,
    }
}
